import {
  useState,
  createContext,
  useContext,
  Dispatch,
  SetStateAction,
  FC,
  ReactNode,
  useEffect,
  useCallback,
} from 'react';
import {VideoFrame} from './components/VideoFrame';
import {Impressum} from './components/Impressum';
import Cookies from './components/Cookies';
import {FirebaseApp, initializeApp} from 'firebase/app';
import {Firestore, getFirestore} from '@firebase/firestore';
import {getStorage, FirebaseStorage} from '@firebase/storage';
import {
  getAnalytics,
  setConsent,
  setAnalyticsCollectionEnabled,
  Analytics,
} from '@firebase/analytics';
import {useLocation} from 'react-router-dom';
import {
  getFunctions,
  Functions,
  connectFunctionsEmulator,
} from '@firebase/functions';

type IUseAppProvider = {
  modalVideoUrl: string | null;
  setModalVideoUrl: Dispatch<SetStateAction<string | null>>;
  impressumOpen: boolean;
  setImpressumOpen: Dispatch<SetStateAction<boolean>>;
  cookiesOpen: boolean;
  setCookiesOpen: Dispatch<SetStateAction<boolean>>;
  useAnalytics: boolean;
  allowAnalytics: () => void;
  declineAnalytics: () => void;
  toggleAnalytics: () => void;
  setCurrentCookieSettings: () => void;
  hideBecauseInfoPage: boolean;
  cookiesAccepted: undefined | boolean;
  app: FirebaseApp | null;
  db: Firestore | null;
  storage: FirebaseStorage | null;
  analytics: Analytics | null;
  functions: Functions | null;
};

const firebaseConfig = {
  apiKey: 'AIzaSyCOpiE12byogZAlfte5P4AKCXTKrNS3OvQ',
  authDomain: 'centrefilms-6cb22.firebaseapp.com',
  projectId: 'centrefilms-6cb22',
  storageBucket: 'centrefilms-6cb22.appspot.com',
  messagingSenderId: '517457726259',
  appId: '1:517457726259:web:38ccb7537046a2d9638c46',
  measurementId: 'G-0QFH6BVV5M',
};

const AppContext = createContext({} as IUseAppProvider);
export const useApp = () => useContext(AppContext);

const useAppProvider = (): IUseAppProvider => {
  const {pathname} = useLocation();
  const [modalVideoUrl, setModalVideoUrl] = useState<string | null>(null);
  const [impressumOpen, setImpressumOpen] = useState<boolean>(false);
  const [cookiesOpen, setCookiesOpen] = useState<boolean>(false);
  const [useAnalytics, setUseAnalytics] = useState<boolean>(false);
  const [cookiesAccepted, setCookiesAccepted] = useState<undefined | boolean>(
    undefined
  );
  const [hideBecauseInfoPage, setHideBecauseInfoPage] =
    useState<boolean>(false);
  const [app, setApp] = useState<FirebaseApp | null>(null);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [db, setDb] = useState<Firestore | null>(null);
  const [storage, setStorage] = useState<FirebaseStorage | null>(null);
  const [functions, setFunctions] = useState<Functions | null>(null);

  useEffect(() => {
    if (cookiesAccepted) {
      setApp(
        initializeApp(firebaseConfig, {automaticDataCollectionEnabled: false})
      );
    }
  }, [cookiesAccepted]);

  useEffect(() => {
    if (app !== null) {
      setAnalytics(getAnalytics(app));
      setDb(getFirestore(app));
      setStorage(getStorage(app));
      const fnc = getFunctions(app, 'europe-west3');
      if (process.env.NODE_ENV === 'development') {
        connectFunctionsEmulator(fnc, 'localhost', 5001);
      }
      setFunctions(fnc);
    }
  }, [app]);

  useEffect(() => {
    if (pathname === '/impressum' || pathname === '/datenschutz') {
      setHideBecauseInfoPage(true);
    } else {
      setHideBecauseInfoPage(false);
    }
  }, [pathname]);

  const allowAnalytics = useCallback(() => {
    setUseAnalytics(true);
    setConsent({
      analytics_storage: 'granted',
      ad_storage: 'denied',
      functionality_storage: 'granted',
      security_storage: 'denied',
      personalization_storage: 'denied',
    });
    localStorage.setItem('google-analytics-consent', 'true');
    if (analytics) {
      setAnalyticsCollectionEnabled(analytics, true);
    }
    setCookiesAccepted(true);
  }, [analytics]);

  const declineAnalytics = useCallback(() => {
    setUseAnalytics(false);
    setConsent({
      analytics_storage: 'granted',
      ad_storage: 'denied',
      functionality_storage: 'denied',
      security_storage: 'denied',
      personalization_storage: 'denied',
    });
    localStorage.setItem('google-analytics-consent', 'false');
    if (analytics) {
      setAnalyticsCollectionEnabled(analytics, false);
    }

    setCookiesAccepted(true);
  }, [analytics]);

  const toggleAnalytics = useCallback(() => {
    setUseAnalytics((prev) => !prev);
    setConsent({
      analytics_storage: 'granted',
      ad_storage: 'denied',
      functionality_storage: useAnalytics ? 'granted' : 'denied',
      security_storage: 'denied',
      personalization_storage: 'denied',
    });
    localStorage.setItem(
      'google-analytics-consent',
      useAnalytics ? 'true' : 'false'
    );
    if (analytics) {
      setAnalyticsCollectionEnabled(analytics, useAnalytics);
    }

    setCookiesAccepted(true);
  }, [analytics, useAnalytics]);

  useEffect(() => {
    // check if google analytics is granted or denied
    const consent = localStorage.getItem('google-analytics-consent');
    if (consent === 'true') {
      allowAnalytics();
      setCookiesAccepted(true);
    } else if (consent === 'false') {
      declineAnalytics();
      setCookiesAccepted(true);
    } else {
      setCookiesOpen(true);
    }
  }, [allowAnalytics, declineAnalytics]);

  const setCurrentCookieSettings = useCallback(() => {
    setConsent({
      analytics_storage: 'granted',
      ad_storage: 'denied',
      functionality_storage: useAnalytics ? 'granted' : 'denied',
      security_storage: 'denied',
      personalization_storage: 'denied',
    });
    localStorage.setItem(
      'google-analytics-consent',
      useAnalytics ? 'true' : 'false'
    );
    setCookiesAccepted(true);
    if (analytics) {
      setAnalyticsCollectionEnabled(analytics, useAnalytics);
    }
  }, [analytics, useAnalytics]);

  return {
    setCurrentCookieSettings,
    cookiesAccepted,
    toggleAnalytics,
    useAnalytics,
    allowAnalytics,
    declineAnalytics,
    db,
    storage,
    functions,
    app,
    impressumOpen,
    setImpressumOpen,
    cookiesOpen,
    setCookiesOpen,
    analytics,
    modalVideoUrl,
    setModalVideoUrl,
    hideBecauseInfoPage,
  };
};

export const AppProvider: FC<{children?: ReactNode}> = ({children}) => {
  const app = useAppProvider();
  return (
    <AppContext.Provider value={app}>
      {children}
      <VideoFrame />
      <Impressum />
      <Cookies />
    </AppContext.Provider>
  );
};
